import { useState, useEffect, useCallback } from 'react';
import { 
  AlertTriangle, Clock, FileText, Phone, CheckCircle, 
  Eye, Users, Zap, ChevronRight, Shield, RefreshCw,
  AlertCircle, ArrowRight, Inbox
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminInbox - Vista operativa "Cosa fare oggi"
 * 
 * Mostra priorità operative invece di statistiche decorative:
 * - Provider da rivedere (urgent)
 * - Documenti in attesa
 * - Categorie sensibili che richiedono contatto
 * - Fast-track approvabili
 * - Richieste bloccate
 */
export function AdminInbox({ onSelectProvider, onFilterChange }) {
  const [inboxData, setInboxData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSection, setExpandedSection] = useState('urgent');

  const loadInboxData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/providers/inbox`);
      if (!res.ok) throw new Error('Failed to load inbox');
      const data = await res.json();
      setInboxData(data);
    } catch (err) {
      console.error('Failed to load inbox:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInboxData();
  }, [loadInboxData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-6 w-6 animate-spin text-umbria-olive" />
      </div>
    );
  }

  if (!inboxData) {
    return null;
  }

  const sections = [
    {
      id: 'urgent',
      title: 'Azioni Urgenti',
      icon: AlertTriangle,
      color: 'red',
      items: [
        ...(inboxData.sensitive_pending || []).map(p => ({
          ...p,
          urgency: 'sensitive',
          label: 'Categoria sensibile - richiede contatto',
          action: 'Verifica obbligatoria'
        })),
        ...(inboxData.docs_pending || []).slice(0, 3).map(p => ({
          ...p,
          urgency: 'docs',
          label: 'Documenti mancanti',
          action: 'Sollecita documenti'
        }))
      ],
      count: (inboxData.counts?.sensitive_pending || 0) + (inboxData.counts?.docs_pending || 0),
      emptyMessage: 'Nessuna azione urgente'
    },
    {
      id: 'review',
      title: 'Da Rivedere Oggi',
      icon: Eye,
      color: 'amber',
      items: (inboxData.under_review || []).slice(0, 5).map(p => ({
        ...p,
        urgency: 'review',
        label: `In revisione da ${p.days_pending || '?'} giorni`,
        action: 'Completa revisione'
      })),
      count: inboxData.counts?.under_review || 0,
      emptyMessage: 'Nessun provider in revisione'
    },
    {
      id: 'fasttrack',
      title: 'Fast-Track Disponibili',
      icon: Zap,
      color: 'green',
      items: (inboxData.fast_track_ready || []).slice(0, 5).map(p => ({
        ...p,
        urgency: 'fasttrack',
        label: 'Documenti completi - approvabile',
        action: 'Approva rapido'
      })),
      count: inboxData.counts?.fast_track_ready || 0,
      emptyMessage: 'Nessun fast-track disponibile'
    },
    {
      id: 'contact',
      title: 'Da Contattare',
      icon: Phone,
      color: 'purple',
      items: (inboxData.contact_pending || []).slice(0, 5).map(p => ({
        ...p,
        urgency: 'contact',
        label: 'In attesa di contatto telefonico',
        action: 'Chiama'
      })),
      count: inboxData.counts?.contact_pending || 0,
      emptyMessage: 'Nessun contatto in sospeso'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      red: { bg: 'bg-red-50', border: 'border-red-200', icon: 'text-red-600', badge: 'bg-red-100 text-red-700' },
      amber: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'text-amber-600', badge: 'bg-amber-100 text-amber-700' },
      green: { bg: 'bg-green-50', border: 'border-green-200', icon: 'text-green-600', badge: 'bg-green-100 text-green-700' },
      purple: { bg: 'bg-purple-50', border: 'border-purple-200', icon: 'text-purple-600', badge: 'bg-purple-100 text-purple-700' }
    };
    return colors[color] || colors.amber;
  };

  return (
    <div className="space-y-4" data-testid="admin-inbox">
      {/* Header with summary */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-umbria-olive/10 rounded-lg">
            <Inbox className="h-6 w-6 text-umbria-olive" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-umbria-charcoal">Inbox Operativa</h2>
            <p className="text-sm text-gray-500">
              {inboxData.total_actionable || 0} azioni in attesa
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={loadInboxData}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Aggiorna
        </Button>
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {sections.map((section) => {
          const colorClasses = getColorClasses(section.color);
          const Icon = section.icon;
          return (
            <button
              key={section.id}
              onClick={() => setExpandedSection(section.id)}
              className={`p-4 rounded-lg border-2 transition-all text-left ${
                expandedSection === section.id 
                  ? `${colorClasses.bg} ${colorClasses.border}` 
                  : 'bg-white border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon className={`h-4 w-4 ${colorClasses.icon}`} />
                <span className={`text-2xl font-bold ${colorClasses.icon}`}>
                  {section.count}
                </span>
              </div>
              <p className="text-xs text-gray-600 truncate">{section.title}</p>
            </button>
          );
        })}
      </div>

      {/* Expanded Section */}
      {sections.map((section) => {
        if (section.id !== expandedSection) return null;
        
        const colorClasses = getColorClasses(section.color);
        const Icon = section.icon;
        
        return (
          <Card key={section.id} className={`${colorClasses.border} border-2`}>
            <CardHeader className={`${colorClasses.bg} py-3`}>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Icon className={`h-5 w-5 ${colorClasses.icon}`} />
                  {section.title}
                  <Badge className={colorClasses.badge}>{section.count}</Badge>
                </CardTitle>
                {section.count > 5 && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => onFilterChange?.(section.id)}
                  >
                    Vedi tutti
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="py-3">
              {section.items.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-400" />
                  <p>{section.emptyMessage}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {section.items.map((item, idx) => (
                    <div
                      key={item.id || idx}
                      onClick={() => onSelectProvider?.(item.id)}
                      className="flex items-center justify-between p-3 rounded-lg bg-white border border-gray-100 hover:border-gray-300 cursor-pointer transition-all group"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-umbria-charcoal truncate">
                            {item.business_name || item.name}
                          </h4>
                          {item.sensitive_category && (
                            <Badge className="bg-red-100 text-red-700 text-xs">
                              <Shield className="h-3 w-3 mr-1" />
                              Sensibile
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">{item.label}</p>
                        {item.type && (
                          <p className="text-xs text-gray-400">
                            {item.type} • {item.comune || item.macro_area || 'Umbria'}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-umbria-olive font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                          {item.action}
                        </span>
                        <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-umbria-olive" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* Recent Activity Footer */}
      {inboxData.recent_approvals?.length > 0 && (
        <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-100">
          <h4 className="text-sm font-medium text-green-800 mb-2 flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            Approvati di recente
          </h4>
          <div className="flex flex-wrap gap-2">
            {inboxData.recent_approvals.slice(0, 5).map((p, idx) => (
              <Badge key={idx} variant="secondary" className="bg-green-100 text-green-700">
                {p.business_name}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
